import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import type { Messages } from '@/features/language/messages'
import { useMessages } from '@/features/language/use-language'
import { startIdentityLink } from '@/features/login-method/api'
import {
  readLinkOutcome,
  withoutLinkOutcome,
  type LinkOutcome,
} from '@/features/login-method/outcome'
import { useLoginMethods } from '@/features/login-method/queries'
import { Notice } from '@/shared/ui/Notice'
import { OutlineButton } from '@/shared/ui/OutlineButton'
import { ProviderButton } from '@/shared/ui/ProviderButton'
import { StatusMessage } from '@/shared/ui/StatusMessage'
import { PROVIDER_IDS, type ProviderId } from '@/shared/ui/providers'

/**
 * 가입 직후 한 번 서는 화면.
 *
 * 추가하지 않은 provider는 다른 공간이 된다. 그 사실을 알 자리가 달리 없어 계정이 방금 선 이
 * 순간에 한 번 알린다. 맞아들이는 인사를 머리에 얹어 화면 하나로 끝내고 이 자리가 사무적으로
 * 읽히지 않게 한다.
 *
 * 추가하겠느냐고 묻지 않는다. 묻는 형태로 두면 대답을 요구하게 되고 건너뛰기가 거절이 된다.
 * 알리고 버튼을 놓아 두는 데서 멈춘다.
 *
 * 추가를 마치면 이 화면이 서지 않는다. Backend가 홈 주소로 돌려보내고 그 자리에서 홈이 선다.
 * 여기로 돌아오는 것은 취소했거나 막혀서 할 일이 남은 경우뿐이다.
 *
 * 어느 provider로 들어왔는지는 서버가 아는 것을 읽어 그린다. 주소에 실린 값으로 그리면 손으로
 * 고친 주소가 엉뚱한 이름을 세운다.
 */
export function WelcomePage() {
  const m = useMessages()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  /* 왕복 결과는 첫 렌더에서 한 번 읽어 두고 주소에서는 곧바로 지운다. */
  const [outcome] = useState(() => readLinkOutcome(searchParams))
  const [leavingProvider, setLeavingProvider] = useState<ProviderId | null>(null)

  const loginMethods = useLoginMethods(true)

  useEffect(() => {
    const next = withoutLinkOutcome(searchParams)
    if (next) setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  if (loginMethods.isPending) {
    return <StatusMessage>{m.auth.gate.opening}</StatusMessage>
  }

  const methods = loginMethods.data?.loginMethods ?? []
  const linked = PROVIDER_IDS.find((id) => methods.some((it) => it.providerId === id && it.linked))
  const missing = PROVIDER_IDS.find((id) =>
    methods.some((it) => it.providerId === id && !it.linked),
  )

  /*
   * 권할 것이 없으면 이 화면이 설 까닭이 없다. 목록을 읽지 못한 경우도 같다. 무엇을 권해야
   * 하는지 모르는 채로 버튼을 세우느니 홈으로 보낸다. 설정에 같은 자리가 남아 있다.
   */
  if (!linked || !missing) {
    return <Navigate to="/" replace />
  }

  const linkedName = m.auth.provider[linked]
  const missingName = m.auth.provider[missing]
  const notice = welcomeNotice(m, outcome)
  const leaving = leavingProvider !== null

  return (
    <main className="bg-canvas text-ink mx-auto flex h-full w-full max-w-[390px] flex-col px-6 pt-8 pb-7">
      <div className="min-h-18 flex-1" />

      <div className="flex flex-col gap-4">
        <h1 className="text-2xl leading-[1.35] font-semibold tracking-[-0.01em]">
          {m.onboarding.greeting}
        </h1>
        <div className="flex flex-col gap-1.5">
          <p className="text-base leading-[1.65]">{m.onboarding.from(linkedName)}</p>
          <p className="text-ink-muted text-base leading-[1.65]">
            {m.onboarding.suggest(missingName)}
          </p>
        </div>
      </div>

      <div className="min-h-14 flex-[1.2]" />

      {/* 진행 문구와 안내 문구가 알림 영역 하나를 쓴다. 두 안내가 함께 뜨지 않는다. */}
      <div aria-live="polite" className="empty:hidden">
        {notice && !leaving && (
          <div className="mb-3">
            <Notice>{notice}</Notice>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <ProviderButton
          provider={missing}
          pending={leaving}
          label={m.onboarding.add(missingName)}
          onClick={() => {
            setLeavingProvider(missing)
            startIdentityLink(missing, 'onboarding')
          }}
        />
        {/*
         * 건너뛰기를 흐린 색으로 낮추지 않는다. 낮추면 고르지 말라는 신호가 되고 건너뛴 사람이
         * 손해를 봤다는 인상이 남는다. 면을 채우지 않아 두 액션이 서로 다투지 않게만 한다.
         */}
        <OutlineButton
          block
          disabled={leaving}
          onClick={() => {
            void navigate('/', { replace: true })
          }}
        >
          {m.onboarding.skip}
        </OutlineButton>
      </div>

      <p className="text-ink-muted mt-4 text-center text-sm leading-[1.6]">{m.onboarding.later}</p>
    </main>
  )
}

function welcomeNotice(m: Messages, outcome: LinkOutcome): string | null {
  if (!outcome || outcome.kind === 'added') return null
  if (outcome.kind === 'failed') return m.settings.loginMethod.failed
  return m.settings.loginMethod.alreadyLinked(m.auth.provider[outcome.provider])
}
